/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../core/viewmodel/ViewModel';
import { Column, COLUMN_FORMAT } from '../../../../types/course/CourseColumn';
import { MenuItemType } from '../../../../types/course/CourseMenuItem';
import { MaterialMenu } from '../../../../models/material/MaterialMenu';
import { Course } from '../../../../types/course/Course';
import { SnackbarStore } from '../../../../stores/application/SnackbarStore';
import { AuthStore } from '../../../../stores/application/AuthStore';
import { CourseService } from '../../../../services/CourseService';
import { StoreProvider } from '../../../../stores/StoreProvider';
import * as H from 'history';
import { ModuleEditingModel } from '../../../../models/components/ModuleEditingModel';
import { useParams } from 'react-router-dom';
import { AddModuleDialogStore } from '../../../../stores/components/AddModuleDialogStore';
import { CourseStore } from '../../../../stores/components/CourseStore';
import { MaterialCircularProgress } from '../../../../models/material/MaterialCircularProgress';
import { ModulePackage } from '../../../../types/course/ModulePackage';

export class ModuleEditingViewModel extends ViewModel {
	public menu: MaterialMenu = new MaterialMenu();
	private circularProgress = new MaterialCircularProgress();

	private readonly snackbarStore: SnackbarStore;
	private readonly authStore: AuthStore;
	private readonly addModuleDialogStore: AddModuleDialogStore;
	private readonly courseStore: CourseStore;

	private routerParams: { courseId?: string } = useParams();
	private moduleEditingModel: ModuleEditingModel = new ModuleEditingModel();
	private courseService: CourseService;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.snackbarStore = storeProvider.snackbarStore;
		this.addModuleDialogStore = storeProvider.addModuleDialogStore;
		this.authStore = storeProvider.authStore;
		this.courseStore = storeProvider.courseStore;
		this.courseService = storeProvider.courseService;
		this.queryCourse(this.routerParams.courseId);
		this.queryCourseModulePackages(this.routerParams.courseId);
	}

	public columns: Column[] = [
		{ id: 'name', title: 'modules.module_editing.table.module_name' },
		{
			id: 'description',
			title: 'modules.module_editing.table.module_description',
			format: COLUMN_FORMAT.SHORTEN,
		},
	];

	public getMenuItems(): MenuItemType<ModulePackage>[] {
		return this.hasCourseStarted()
			? []
			: [
					{
						menuText: 'modules.module_editing.remove_module',
						menuFunction: (row: ModulePackage | undefined): void => {
							if (row) {
								this.removeModulePackage(row.id);
								this.menu.closeMenu();
							}
						},
					},
				];
	}

	public isLoading(): boolean {
		return this.circularProgress.isLoading;
	}

	private loadHasCourseStarted(): void {
		const muiStartDate = this.storeProvider.courseStore.getCourse().startDate;
		if (muiStartDate) {
			const startDate = new Date(muiStartDate);
			this.storeProvider.courseStore.setHasCourseStarted(startDate < new Date());
		}
	}

	private loadCourseCanBeEdited(): void {
		const muiStartDate = this.storeProvider.courseStore.getCourse().startDate;
		if (muiStartDate) {
			const startDate = new Date(muiStartDate);
			const tomorrow: Date = new Date(startDate.setDate(startDate.getDate() + 1));
			tomorrow.setHours(24);
			this.storeProvider.courseStore.setCanCourseBeEdited(new Date() < tomorrow);
		}
	}

	public hasCourseStarted(): boolean {
		return this.storeProvider.courseStore.hasCourseStarted();
	}

	public canCourseBeEdited(): boolean {
		return this.storeProvider.courseStore.canCourseBeEdited();
	}

	public getCourseModulePackages(): ModulePackage[] {
		return this.addModuleDialogStore.getCourseModulePackages();
	}

	public setCourse(course: Course): void {
		this.courseStore.setCourse(course);
	}

	public setModulePackage(modulePackage: ModulePackage): void {
		this.moduleEditingModel.setModulePackage(modulePackage);
	}

	public getModulePackage(): ModulePackage {
		return this.moduleEditingModel.modulePackage;
	}

	public isDetailModulePackageSet(): boolean {
		return this.moduleEditingModel.modulePackage.id !== '';
	}

	public setDisableButton(bool: boolean): void {
		this.moduleEditingModel.setDisableAddModuleButton(bool);
	}

	public getDisableButton(): boolean {
		return this.canCourseBeEdited();
	}

	public queryCourse(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();

		return this.courseService
			.getCourse(authToken, courseId)
			.then((course: Course) => {
				this.setCourse(course);
				this.loadHasCourseStarted();
				this.loadCourseCanBeEdited();
			})
			.catch(() => {
				this.snackbarStore.showError('students.query_error');
			});
	}

	public queryCourseModulePackages(courseId: string): Promise<void> | void {
		this.circularProgress.setIsLoading(true);
		const authToken: string = this.authStore.getAuthToken();

		return this.courseService
			.getCourseModulePackages(authToken, courseId)
			.then((modulePackages: ModulePackage[]) => {
				this.setCourseModulePackages(modulePackages);
				this.circularProgress.setIsLoading(false);
			})
			.catch(() => {
				this.snackbarStore.showError('modules.query_error');
			});
	}

	private setCourseModulePackages(modulePackages: ModulePackage[]): void {
		this.addModuleDialogStore.setCourseModulePackages(modulePackages);
	}

	public removeModulePackage(modulePackageId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();
		const courseId = this.routerParams.courseId;

		return this.courseService
			.removeCourseModulePackage(courseId, modulePackageId, authToken)
			.then(() => {
				this.queryCourseModulePackages(courseId);
				this.snackbarStore.showSuccess('modules.module_editing.module_removed');
			})
			.catch(() => {
				this.snackbarStore.showError('modules.module_editing.module_removed_error');
			});
	}

	public openAddModuleDialog(): void {
		this.addModuleDialogStore.openAddModuleDialog();
	}

	public isAddModuleDialogOpen(): boolean {
		return this.addModuleDialogStore.isAddModuleDialogOpen();
	}
}
